'use client';

import React from 'react';
import { INVESTOR_COUNT_CLAIM } from '@/lib/approx';
import { useTranslation } from 'react-i18next';

interface PaperPlaneAnimationProps {
  className?: string;
}

// Predefined positions for mail icons to prevent hydration mismatches
const MAIL_POSITIONS = [
  { x: 240, y: 143 },
  { x: 117, y: 193 },
  { x: 112, y: 441 },
  { x: 238, y: 125 },
  { x: 111, y: 319 }
];

// Fixed animation durations
const ANIMATION_DURATIONS = [3.2, 3.4, 3.6, 3.8, 4.0];

const PaperPlaneAnimation: React.FC<PaperPlaneAnimationProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  return (
    <div className={`relative ${className}`}>
      <svg 
        viewBox="0 0 500 500" 
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
      >
        {/* Background circle */}
        <circle cx="250" cy="250" r="200" fill="#f1f5f9" />
        
        {/* Animation path for the plane */}
        <path
          id="planePath"
          d="M100,250 C100,150 150,100 250,100 C350,100 400,150 400,250 C400,350 350,400 250,400 C150,400 100,350 100,250"
          fill="none"
          stroke="transparent"
        />
        
        {/* Paper plane — CSS motion-path animation (compositor-friendly).
            The previous SMIL <animateMotion> ran on the main thread and caused
            continuous style/layout work that tanked mobile performance. */}
        <g>
          <path
            d="M-20,-20 L20,0 L-20,20 L-10,0 Z"
            fill="#4f46e5"
            stroke="#3730a3"
            strokeWidth="1"
            className="pp-plane"
          />
        </g>
        
        {/* Mail icons scattered around — CSS opacity animation (was SMIL) */}
        {MAIL_POSITIONS.map((pos, i) => (
          <g key={i} className={`mail-icon-${i}`}>
            <rect
              x={pos.x}
              y={pos.y}
              width="20"
              height="15"
              rx="2"
              fill="#cbd5e1"
              opacity="0.7"
              className="pp-mail"
              style={{ animationDuration: `${ANIMATION_DURATIONS[i]}s` }}
            />
          </g>
        ))}
        
        {/* Curved dotted line following the path */}
        <path
          d="M100,250 C100,150 150,100 250,100 C350,100 400,150 400,250 C400,350 350,400 250,400 C150,400 100,350 100,250"
          fill="none"
          stroke="#cbd5e1"
          strokeDasharray="5,5"
          strokeWidth="2"
          opacity="0.6"
        />
        
        {/* Icons in the four corners representing investors */}
        {[
          { x: 150, y: 150 },
          { x: 350, y: 150 },
          { x: 350, y: 350 },
          { x: 150, y: 350 }
        ].map((pos, i) => (
          <g key={i}>
            <circle 
              cx={pos.x} 
              cy={pos.y} 
              r="15" 
              fill="#e0e7ff" 
              stroke="#c7d2fe" 
              strokeWidth="1" 
            />
            <text
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#4f46e5"
              fontSize="12"
              fontWeight="bold"
            >
              $
            </text>
          </g>
        ))}
      </svg>
      
      {/* Overlay text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center">
          {/* Same constant every other surface uses, so the animation cannot
              drift from the claim. See lib/approx.ts. */}
          <div className="text-4xl font-bold text-primary-500">{INVESTOR_COUNT_CLAIM}</div>
          <div className="text-gray-600" suppressHydrationWarning>{t('components.paperPlaneAnimation.investors')}</div>
        </div>
      </div>
    </div>
  );
};

export { PaperPlaneAnimation };